import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { WeeklySlot } from '@/lib/publishQueues/types';
import { isValidWeeklySlot, timeToMinutes, weeklySlotTimeLabel } from '@/lib/publishQueues/weeklySlots';
import { CALENDAR_HEIGHT, CALENDAR_HOUR_HEIGHT } from '@/lib/publishQueues/calendarSelection';
import { editCalendarSlot } from '@/lib/publishQueues/editCalendarSlot';
import { layoutCalendarSlots } from '@/lib/publishQueues/layoutCalendarSlots';
import { CalendarSlotBlock } from './CalendarSlotBlock';
import { useWeeklyScheduleDrag } from './useWeeklyScheduleDrag';

type Weekday = { weekday: number; label: string; shortLabel: string };

type Props = {
  slots: WeeklySlot[];
  weekdays: Weekday[];
  onSelectSlot: (index: number, edge?: 'start' | 'end') => void;
  onUpdateSlot: (index: number, slot: WeeklySlot) => void;
  onAddSlot: (slot: WeeklySlot) => void;
  /** Omit to hide the calendar's delete controls (e.g. only one slot is left). */
  onRemoveSlot?: (index: number) => void;
};

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

export function WeeklySchedulePreview({ slots, weekdays, onSelectSlot, onAddSlot, onUpdateSlot, onRemoveSlot }: Props) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useWeeklyScheduleDrag({ scrollRef, onAddSlot, onUpdateSlot });
  const positioned = useRef(false);
  const earliestMinute = Math.min(...slots.filter(isValidWeeklySlot).map((slot) => timeToMinutes(slot.timeLocal)));
  const firstMinute = Number.isFinite(earliestMinute) ? earliestMinute : 9 * 60;
  useEffect(() => {
    if (positioned.current) return;
    // Wait for the form's initial queue hydration before positioning the calendar once.
    const frame = requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = Math.max(0, firstMinute / 60 - 1) * CALENDAR_HOUR_HEIGHT;
      positioned.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [firstMinute]);
  const pendingSlot = drag.preview?.slot;
  const windows = slots.flatMap((slot, index) => isValidWeeklySlot(slot) ? [{
    slot, index, start: timeToMinutes(slot.timeLocal),
    end: timeToMinutes(slot.endTimeLocal ?? slot.timeLocal),
  }] : []);
  const hourHeight = CALENDAR_HOUR_HEIGHT;

  return (
    <div className="min-w-0 space-y-2">
      <div ref={scrollRef} role="region" aria-label={t('publishQueues.form.weekPreview')}
        aria-describedby="queue-calendar-help" {...drag.handlers}
        className="h-[360px] min-w-0 max-w-full overflow-auto overscroll-contain rounded-xl border bg-background" tabIndex={0}>
        <div className="min-w-[600px]">
          <div className="sticky top-0 z-20 grid grid-cols-[2.75rem_repeat(7,minmax(0,1fr))] border-b bg-card">
            <div aria-hidden="true" className="sticky left-0 bg-card" />
            {weekdays.map((day) => (
              <div key={day.weekday} title={day.label}
                className="border-l py-2.5 text-center text-xs font-medium capitalize text-muted-foreground">
                {day.shortLabel}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-[2.75rem_repeat(7,minmax(0,1fr))]">
            <div aria-hidden="true" className="sticky left-0 z-10 bg-background">
              {HOURS.map((hour) => (
                <div key={hour} style={{ height: hourHeight }} className="pr-2 pt-1 text-right text-[10px] tabular-nums text-muted-foreground">
                  {`${String(hour).padStart(2, '0')}:00`}
                </div>
              ))}
            </div>
            {weekdays.map((day) => {
              const { blocks, laneCount } = layoutCalendarSlots(
                windows.filter((window) => window.slot.weekday === day.weekday), (28 / hourHeight) * 60,
              );
              return (
                <div key={day.weekday} data-weekday={day.weekday} tabIndex={-1}
                  className="relative touch-pan-x select-none border-l outline-none"
                  onPointerDown={(event) => drag.startCreate(event, day.weekday)}
                  style={{ height: CALENDAR_HEIGHT }}>
                  {HOURS.map((hour) => (
                    <div key={hour} aria-hidden="true" className="border-t border-border/50 after:block after:h-1/2 after:border-b after:border-dashed after:border-border/30" style={{ height: hourHeight }} />
                  ))}
                  {blocks.map(({ slot, index, start, end, lane }) => (
                    <CalendarSlotBlock key={index} index={index} slot={slot} dayLabel={day.label}
                      editing={drag.preview?.index === index} onPointerStart={drag.startEdit}
                      consumeClick={drag.consumeClick}
                      onRemove={onRemoveSlot && (() => {
                        onRemoveSlot(index);
                        // The focused block is gone; keep keyboard focus inside the calendar.
                        scrollRef.current?.focus({ preventScroll: true });
                      })}
                      onSelect={(edge) => edge ? onSelectSlot(index, edge) : onSelectSlot(index)}
                      onKeyboardEdit={(mode, event) => {
                        const delta = event.key === 'ArrowUp' ? -5 : event.key === 'ArrowDown' ? 5 : 0;
                        const dayDelta = mode === 'move' ? (event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0) : 0;
                        if (!delta && !dayDelta) return;
                        event.preventDefault();
                        if (drag.preview) return;
                        onUpdateSlot(index, editCalendarSlot(slot, mode, delta, Math.max(0, Math.min(6, slot.weekday + dayDelta))));
                        // Crossing days remounts the block in another column. Keep keyboard editing active.
                        if (dayDelta) requestAnimationFrame(() => {
                          scrollRef.current?.querySelector<HTMLElement>(`[data-slot-body="${index}"]`)?.focus({ preventScroll: true });
                        });
                      }}
                      style={{
                        top: Math.min((start / 60) * hourHeight, CALENDAR_HEIGHT - 28),
                        height: Math.max(((end - start) / 60) * hourHeight, 28),
                        left: `calc(${(lane / laneCount) * 100}% + 2px)`,
                        width: `calc(${100 / laneCount}% - 4px)`,
                      }} />
                  ))}
                  {pendingSlot?.weekday === day.weekday && (
                    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0.5 rounded border-2 border-primary bg-primary/25 px-1 text-[10px] font-semibold tabular-nums text-primary"
                      style={{ top: Math.min(timeToMinutes(pendingSlot.timeLocal) / 60 * hourHeight, CALENDAR_HEIGHT - 28),
                        height: Math.max((timeToMinutes(pendingSlot.endTimeLocal ?? pendingSlot.timeLocal) - timeToMinutes(pendingSlot.timeLocal)) / 60 * hourHeight, 28) }}>
                      <span className="whitespace-nowrap rounded bg-background/90 px-0.5">{weeklySlotTimeLabel(pendingSlot)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p id="queue-calendar-help" className="text-xs leading-relaxed text-muted-foreground">
        {t('publishQueues.form.dragHint')}
      </p>
      <p role="status" aria-live="polite" className="sr-only">
        {pendingSlot ? `${weekdays.find((day) => day.weekday === pendingSlot.weekday)?.label} ${weeklySlotTimeLabel(pendingSlot)}` : ''}
      </p>
    </div>
  );
}
